import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { DictionaryName, lang } from '@sc/data-access-models';
import { dayjs } from '@sc/utils';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TranslatorService {
  private currentDictionary: DictionaryName = 'default';
  private readonly currentLang$ = new BehaviorSubject<lang>('fr_FR');
  public langChange$ = new BehaviorSubject<LangChangeEvent>(null);

  constructor(
    private readonly translate: TranslateService,
    private readonly configService: ConfigurationService
  ) {}

  init$() {
    this.setDictionary(this.configService.getConfig().dictionary);

    this.subscribeToLangChange();

    return this.use(this.configService.getConfig().defaultLang);
  }

  getCurrentDictionary() {
    return this.currentDictionary;
  }

  getCurrentLang() {
    return this.currentLang$;
  }

  getCurrentLangValue(): lang {
    return this.currentLang$.value;
  }

  setDictionary(dictionary: DictionaryName) {
    this.currentDictionary = dictionary;
  }

  setDefaultLang(language: lang) {
    this.translate.setDefaultLang(language);
    this.translate.use(language);
  }

  use(language: lang) {
    return this.translate.use(language);
  }

  private subscribeToLangChange() {
    this.translate.onLangChange
      .pipe(
        map((newLangEvent) => newLangEvent.lang as lang),
        switchMap((newLang) => this.preChangeLang(newLang).pipe(take(1))),
        tap((newLang) => this.currentLang$.next(newLang)),
        switchMap((newLang) => this.postChangeLang(newLang).pipe(take(1)))
      )
      .subscribe();
  }

  private preChangeLang(newLang: lang): Observable<lang> {
    const newLangDayJs = langToDayjsLang(newLang);

    return from(loadLocale(newLangDayJs)).pipe(
      take(1),
      map(() => newLang)
    );
  }

  private postChangeLang(newLang: lang): Observable<lang> {
    this.modifyHTMLLangAttr(newLang);

    return of(newLang);
  }

  private modifyHTMLLangAttr(lang: lang) {
    const langSubTag = lang.split('_')[0];
    const htmlTags = document.getElementsByTagName('html');
    for (let i = 0; i < htmlTags.length; i++) {
      const htmlTag = htmlTags.item(i);
      if (htmlTag.getAttribute('lang')) {
        htmlTag.setAttribute('lang', langSubTag);
      }
    }
  }

  getTranslation(
    key: string | Array<string>,
    interpolateParams?: Object
  ): Observable<any> {
    return this.currentLang$.pipe(
      switchMap(() => this.translate.get(key, interpolateParams))
    );
  }

  getInstantTranslation(
    key: string | Array<string>,
    interpolateParams?: Object
  ) {
    return key ? this.translate?.instant(key, interpolateParams) : '';
  }

  getInstantTranslationOrValue(value: string | null | undefined): string {
    return value && this.instantIsKeyDefined(value)
      ? this.getInstantTranslation(value)
      : value ?? '';
  }

  instantIsKeyDefined(key: string | string[]) {
    const splitKey = (typeof key === 'string' ? key.split('.') : key) || [];

    return !!splitKey.reduce(
      (acc, curr) => acc?.[curr],
      this.translate.translations[this.currentLang$.getValue()]
    );
  }

  isKeyDefined(key: string | string[]) {
    const splitKey = (typeof key === 'string' ? key.split('.') : key) || [];

    return this.currentLang$.pipe(
      map(
        (lang) =>
          !!splitKey.reduce(
            (acc, curr) => acc?.[curr],
            this.translate.translations[lang]
          )
      )
    );
  }

  isKeyDefinedAndEmpty(key: string | string[]) {
    return this.isKeyDefined(key).pipe(
      switchMap((isDefined) => {
        if (isDefined) {
          this.getTranslation(key).pipe(map((translated) => translated === ''));
        } else {
          return of(false);
        }
      })
    );
  }

  getLangChangeEvent() {
    return this.translate.onLangChange;
  }
}

const locales = {
  de: () => import('dayjs/locale/de'),
  el: () => import('dayjs/locale/el'),
  en: () => import('dayjs/locale/en'),
  es: () => import('dayjs/locale/es'),
  et: () => import('dayjs/locale/et'),
  fi: () => import('dayjs/locale/fi'),
  fr: () => import('dayjs/locale/fr'),
  hu: () => import('dayjs/locale/hu'),
  it: () => import('dayjs/locale/it'),
  lt: () => import('dayjs/locale/lt'),
  nb: () => import('dayjs/locale/nb'),
  'pt-br': () => import('dayjs/locale/pt-br'),
  pt: () => import('dayjs/locale/pt'),
  ru: () => import('dayjs/locale/ru'),
  nl: () => import('dayjs/locale/nl'),
} as const;

function loadLocale(language: keyof typeof locales) {
  return locales[language]().then((langModule) => {
    if (language == 'el') {
      dayjs.locale('el', {
        name: 'el',
        weekdays:
          'Κυριακή_Δευτέρα_Τρίτη_Τετάρτη_Πέμπτη_Παρασκευή_Σάββατο'.split('_'),
        weekdaysShort: 'Κυρ_Δευ_Τρι_Τετ_Πεμ_Παρ_Σαβ'.split('_'),
        weekdaysMin: 'Κυ_Δε_Τρ_Τε_Πε_Πα_Σα'.split('_'),
        months: [
          'Ιανουαρίου',
          'Φεβρουαρίου',
          'Μαρτίου',
          'Απριλίου',
          'Μαΐου',
          'Ιουνίου',
          'Ιουλίου',
          'Αυγούστου',
          'Σεπτεμβρίου',
          'Οκτωβρίου',
          'Νοεμβρίου',
          'Δεκεμβρίου',
        ],
        monthsShort: 'Ιαν_Φεβ_Μαρ_Απρ_Μαι_Ιουν_Ιουλ_Αυγ_Σεπτ_Οκτ_Νοε_Δεκ'.split(
          '_'
        ),
        ordinal: (n) => n,
        weekStart: 1,
        relativeTime: {
          future: 'σε %s',
          past: 'πριν %s',
          s: 'μερικά δευτερόλεπτα',
          m: 'ένα λεπτό',
          mm: '%d λεπτά',
          h: 'μία ώρα',
          hh: '%d ώρες',
          d: 'μία μέρα',
          dd: '%d μέρες',
          M: 'ένα μήνα',
          MM: '%d μήνες',
          y: 'ένα χρόνο',
          yy: '%d χρόνια',
        },
        formats: {
          LT: 'h:mm A',
          LTS: 'h:mm:ss A',
          L: 'DD/MM/YYYY',
          LL: 'D MMMM YYYY',
          LLL: 'D MMMM YYYY h:mm A',
          LLLL: 'dddd, D MMMM YYYY h:mm A',
        },
      });
    } else {
      dayjs.locale(langModule);
    }
  });
}

function langToDayjsLang(lang: lang): keyof typeof locales {
  if (lang === 'pt_BR') {
    return 'pt-br';
  }
  return lang.substring(0, 2) as keyof typeof locales;
}
